import { dedent } from 'ts-dedent';
import { ColumnDataType, Provider, WorkflowCodeType, WorkflowComponentParamType, WorkflowComponentParameterMode } from '../types';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class GWR extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.gwr',
            group: 'Statistics',
            title: 'GWR',
            description: `
          This component runs a Geographically Weighted Regression model on a spatial index grid.
        `,
            starred: true
        }, [
            {
                name: 'input_table',
                type: WorkflowComponentParamType.InputTable,
                title: 'Input table'
            },
            {
                name: 'index_column',
                type: WorkflowComponentParamType.Column,
                title: 'Index column',
                parent: 'input_table',
                dataType: [ColumnDataType.String, ColumnDataType.Number]
            },
            {
                name: 'features_columns',
                type: WorkflowComponentParamType.Column,
                title: 'Feature variables',
                parent: 'input_table',
                mode: WorkflowComponentParameterMode.Multiple
            },
            {
                name: 'label_column',
                type: WorkflowComponentParamType.Column,
                title: 'Target variable',
                parent: 'input_table',
                dataType: [ColumnDataType.Number]
            },
            {
                name: 'kring_distance',
                type: WorkflowComponentParamType.Number,
                title: 'Kring size',
                default: 3,
                min: 1
            },
            {
                name: 'kernel_function',
                type: WorkflowComponentParamType.Selection,
                title: 'Kernel function',
                options: [
                    'uniform',
                    'triangular',
                    'quadratic',
                    'quartic',
                    'gaussian'
                ],
                default: 'uniform'
            },
            {
                name: 'fit_intercept',
                type: WorkflowComponentParamType.Boolean,
                title: 'Fit intercept',
                default: true
            }
        ]);
        this.supportedProviders = [Provider.BigQuery, Provider.Snowflake];
    }
    async getCode({ workflow, params, connection, envVars, codeType, schemas }) {
        const preparedParams = this._prepareParameters(params);
        const dryRun = codeType === WorkflowCodeType.DryRun;
        const tablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const at = connection.quoteName(envVars.analyticsToolboxDataset);
        const source = preparedParams.input_table;
        const columns = preparedParams.features_columns;
        let query = '';
        if (dryRun) {
            const cols = `${columns
                .map((v) => '0.1 AS ' + v + '_coef_estimate')
                .join(', ')}${params.fit_intercept ? ', 0.1 AS intercept' : ''}`;
            switch (connection.type) {
                case Provider.BigQuery:
                    query = dedent `
            DECLARE grid_type STRING;
            DECLARE grid_resolution INT64;
            CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
                'SELECT * FROM ${connection.quoteName(source)}',
                '${params.index_column}',
                grid_type,
                grid_resolution
            );
            IF grid_type = 'quadbin' THEN
              ${connection.getCreateTempTableQuery(tablename, dedent ` SELECT
                          5209574053332910079 AS quadbin,
                          ${dedent(cols)}`, codeType, null)}
            ELSE
              ${connection.getCreateTempTableQuery(tablename, dedent ` SELECT
                          '852e6d6bfffffff' AS h3,
                          ${dedent(cols)}`, codeType, null)}
            END IF;`;
                    break;
                case Provider.Snowflake:
                    query = dedent `
            DECLARE
              grid_type STRING;
            BEGIN
              CALL ${at}._CHECK_GRID_INDEX_COLUMN(
                'SELECT * FROM ${source}',
                '${params.index_column}'
              ) INTO grid_type;
              IF (grid_type = 'quadbin') THEN
                ${connection.getCreateTempTableQuery(tablename, dedent ` SELECT
                            5209574053332910079 AS quadbin,
                            ${dedent(columns
                        .map((v) => '0.1 AS ' + v + '_coef_estimate')
                        .join(', '))}`, codeType, null)}
              ELSE
                ${connection.getCreateTempTableQuery(tablename, dedent ` SELECT
                            '852e6d6bfffffff' AS h3,
                            ${dedent(columns
                        .map((v) => '0.1 AS ' + v + '_coef_estimate')
                        .join(', '))}`, codeType, null)}
              END IF;
            END;`;
                    break;
            }
        }
        else {
            switch (connection.type) {
                case Provider.BigQuery:
                    query = dedent `
                DECLARE grid_type STRING;
                DECLARE grid_resolution INT64;
                CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
                    'SELECT * FROM ${connection.quoteName(source)}',
                    '${params.index_column}',
                    grid_type,
                    grid_resolution
                );
                IF grid_type != 'unsupported' THEN
                  CALL ${at}.GWR_GRID(
                    '${params.input_table}',
                    [${columns.map((v) => `'${v}'`).join(', ')}],
                    '${params.label_column}',
                    '${params.index_column}',
                    IF(grid_type = 'h3', 'h3', 'quadbin'),
                    ${params.kring_distance},
                    '${params.kernel_function}',
                    ${params.fit_intercept},
                    '${tablename}'
                  );
                ELSE
                    RAISE USING MESSAGE = 'Invalid spatial index column, please select a column that contains h3 or quadbin indexes.';
                END IF;`;
                    break;
                case Provider.Snowflake:
                    query = dedent `
                DECLARE
                  grid_type STRING DEFAULT 'unsupported';
                  WRONG_GRID_TYPE_EXCEPTION EXCEPTION;
                BEGIN
                  CALL ${at}._CHECK_GRID_INDEX_COLUMN(
                    'SELECT * FROM ${source}',
                    '${params.index_column}'
                  ) INTO grid_type;

                  CASE
                  WHEN (grid_type != 'unsupported') THEN
                    EXECUTE IMMEDIATE '
                        CALL ${at}.GWR_GRID(
                          ''${params.input_table}'',
                          [${columns.map((v) => `''${v}''`).join(', ')}],
                          ''${params.label_column}'',
                          ''${params.index_column}'',
                          ${params.kring_distance},
                          ''${params.kernel_function}'',
                          ${params.fit_intercept},
                          ''${tablename}''
                        );
                    ';
                  ELSE
                    RAISE WRONG_GRID_TYPE_EXCEPTION;
                  END CASE;
                END;`;
                    break;
            }
        }
        query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType);
        return {
            outputs: { result: tablename },
            query
        };
    }
    requiredAnalyticsToolboxVersion(provider) {
        return '1.0.0';
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema({ columns, params }) {
        const featureColumnNames = params.features_columns;
        const schema = featureColumnNames.map((v) => {
            return {
                name: v + '_coef_estimate',
                type: ColumnDataType.Number
            };
        });
        if (params.fit_intercept) {
            schema.push({
                name: 'intercept',
                type: ColumnDataType.Number
            });
        }
        const indexCol = columns.input_table.find((c) => c.name === params.index_column);
        if (indexCol == undefined) {
            return [];
        }
        const indexType = indexCol.type;
        const indexName = indexCol.name;
        schema.push({
            name: indexName,
            type: indexType
        });
        return schema;
    }
}
export { GWR };
